import {useState, useMemo, useCallback} from 'react';
import {useAppDispatch, useAppSelector} from '@/store';
import {setActivityFormData} from '@/store/slices/activitySlice';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {
  handleUnpublishActivity,
  getActivityCsvFile
} from '@/services/activities';
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import {
  ACTIVITY_STATUS,
  UNPUBLISH_BUTTON_VISIBLE_STATUS,
  PUBLISH_BUTTON_VISIBLE_STATUS,
  SURVEY_DUPLICATE_BUTTON_ENABLED_STATUS,
  SURVEY_DOWNLOAD_BUTTON_DISABLED_STATUS
} from '@/utils/constants/activityConstants';
import {ROUTES} from '@/utils/constants/routesConstants';
import {useNavigate} from 'react-router-dom';
import {logger} from '@/helpers/logger';

/**
 * Custom hook encapsulating all survey action button logic:
 * Unpublish/Publish toggle, Duplicate, and Download.
 *
 * @param {Object} options
 * @param {Function} options.handlePublish - Publish handler from useActivityReview
 *   (reuses the same validation, param-building, and API call as the footer).
 */
const useSurveyActions = ({handlePublish} = {}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const activityFormData = useAppSelector((state) => state.activity.formData);

  const [showUnpublishModal, setShowUnpublishModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [actionAlert, setActionAlert] = useState(null);

  const normalizedStatus = activityFormData?.status?.toUpperCase?.() || '';

  const shouldShowUnpublishButton = useMemo(
    () => UNPUBLISH_BUTTON_VISIBLE_STATUS.includes(normalizedStatus),
    [normalizedStatus]
  );

  const shouldShowPublishButton = useMemo(
    () => PUBLISH_BUTTON_VISIBLE_STATUS.includes(normalizedStatus),
    [normalizedStatus]
  );

  const isPublishEnabled = useMemo(() => {
    if (!shouldShowPublishButton) return false;
    const endDate = activityFormData?.endDate;
    if (!endDate) return false;
    return new Date(endDate) > new Date();
  }, [shouldShowPublishButton, activityFormData?.endDate]);

  const isDuplicateEnabled = useMemo(
    () => SURVEY_DUPLICATE_BUTTON_ENABLED_STATUS.includes(normalizedStatus),
    [normalizedStatus]
  );

  const isDownloadEnabled = useMemo(
    () => !SURVEY_DOWNLOAD_BUTTON_DISABLED_STATUS.includes(normalizedStatus),
    [normalizedStatus]
  );

  const handleUnpublishClick = useCallback(() => {
    setShowUnpublishModal(true);
  }, []);

  const handlePublishClick = useCallback(() => {
    setShowPublishModal(true);
  }, []);

  const handleCloseUnpublishModal = useCallback(() => {
    setShowUnpublishModal(false);
  }, []);

  const handleClosePublishModal = useCallback(() => {
    setShowPublishModal(false);
  }, []);

  const handleDismissAlert = useCallback(() => {
    setActionAlert(null);
  }, []);

  const handleConfirmUnpublish = useCallback(async () => {
    setShowUnpublishModal(false);
    try {
      const payload = {
        data: {
          id: activityFormData?.id || activityFormData?.activityId,
          activityType: activityFormData?.activityType,
          status: ACTIVITY_STATUS.DRAFT
        }
      };
      const response = await handleUnpublishActivity(payload);
      if (response?.success) {
        dispatch(
          setActivityFormData({
            ...activityFormData,
            status: ACTIVITY_STATUS.DRAFT
          })
        );
        setActionAlert({
          type: 'caution',
          title: 'Survey Unpublished',
          subTitle:
            'This survey is now paused, You can reactivate it at any time.'
        });
      } else {
        dispatch(
          setToastNotifications({
            show: true,
            message: response?.message || 'Failed to unpublish survey',
            type: 'error'
          })
        );
      }
    } catch (error) {
      logger.error('Error unpublishing survey:', error);
      dispatch(
        setToastNotifications({
          show: true,
          message:
            error?.response?.data?.message ||
            error?.message ||
            'Failed to unpublish survey',
          type: 'error'
        })
      );
    }
  }, [activityFormData, dispatch]);

  const handleConfirmPublish = useCallback(async () => {
    setShowPublishModal(false);
    if (!handlePublish) {
      logger.error('handlePublish not provided to useSurveyActions');
      return;
    }
    await handlePublish({
      onSuccess: () => {
        setActionAlert({
          type: 'success',
          title: 'Survey Successfully Published',
          subTitle: ''
        });
      }
    });
  }, [handlePublish]);

  const handleDuplicate = useCallback(() => {
    const duplicateData = {
      ...activityFormData,
      id: undefined,
      uuid: undefined,
      activityId: undefined,
      status: undefined,
      activityName: ''
    };
    dispatch(setActivityFormData(duplicateData));
    navigate(ROUTES.ACTIVITY_ADD);
  }, [activityFormData, dispatch, navigate]);

  const handleDownload = useCallback(async () => {
    try {
      const activityId = activityFormData?.id || activityFormData?.activityId;
      if (!activityId) return;
      const data = await getActivityCsvFile('', {
        ids: [activityId],
        excludeIds: [],
        filters: []
      });
      const fileName = `Survey_${getDateWithTimeString()}`;
      downloadCSVFiles(data, fileName);
    } catch (error) {
      logger.error('Error downloading survey:', error);
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Failed to download survey',
          type: 'error'
        })
      );
    }
  }, [activityFormData, dispatch]);

  return {
    shouldShowUnpublishButton,
    shouldShowPublishButton,
    isPublishEnabled,
    isDuplicateEnabled,
    isDownloadEnabled,
    showUnpublishModal,
    showPublishModal,
    actionAlert,
    handleUnpublishClick,
    handlePublishClick,
    handleCloseUnpublishModal,
    handleClosePublishModal,
    handleConfirmUnpublish,
    handleConfirmPublish,
    handleDuplicate,
    handleDownload,
    handleDismissAlert
  };
};

export default useSurveyActions;
